import { useCallback, useEffect, useState } from 'react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { api, queryKeys } from '~/api'
import { SettingsLayout, SettingsSection } from '~/settings/components/settings-layout'
import { useAppSelector } from '~/store'
import GroupDeleteConfirm from '../settings/components/Groups/group-delete-confirm'
import GroupDetail from '../settings/components/Groups/group-detail'
import { useTableStyles } from '../theme'
import { sortArrayByField, sortByFieldTextAsc } from '../utils'

export function Groups() {
    const accountOwnerId = useAppSelector((state) => state.initial.accountOwner.id)

    const tableClasses = useTableStyles()
    const [searchTerm, setSearchTerm] = useState('')
    const [groupDetailsOpen, setGroupDetailsOpen] = useState(false)
    const [isCreateGroup, setIsCreateGroup] = useState(false)
    const [selectedGroup, setSelectedGroup] = useState(null)
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
    const { data, isLoading, error } = api.users.accountGroupListing.useQuery(
        queryKeys.users.groups(accountOwnerId).queryKey,
        { params: { id: accountOwnerId } },
        {
            select: (data) => ({
                ...data,
                body: sortArrayByField(data.body, 'name', sortByFieldTextAsc),
            }),
        }
    )

    useEffect(() => {
        if (!deleteConfirmationOpen) {
            setSelectedGroup(null)
        }
    }, [deleteConfirmationOpen])

    useEffect(() => {
        if (!groupDetailsOpen) {
            setSelectedGroup(null)
        }
    }, [groupDetailsOpen])

    const renderGroups = useCallback(() => {
        if (isLoading) {
            return (
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 2,
                    }}
                >
                    <CircularProgress size={30} color="secondary" />
                </Box>
            )
        }

        if (error) {
            return <Typography color="error">{gettext('Something went wrong')}</Typography>
        }

        const filteredGroups = filterGroups(data.body, searchTerm)
        if (filteredGroups.length === 0) {
            return (
                <Box
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}
                >
                    <AccountCircleIcon color="disabled" />
                    <Typography color="textSecondary">{gettext('No groups found')}</Typography>
                </Box>
            )
        }

        return (
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{gettext('Group name')}</TableCell>
                            <TableCell width={50} align="center">
                                {gettext('Edit')}
                            </TableCell>
                            <TableCell width={50} align="center">
                                {gettext('Delete')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredGroups.map((group) => (
                            <TableRow key={group.id}>
                                <TableCell className={tableClasses.tableCell}>
                                    {group.name}
                                </TableCell>
                                <TableCell className={tableClasses.tableCell} align="center">
                                    <IconButton
                                        aria-label="edit"
                                        onClick={() => {
                                            setIsCreateGroup(false)
                                            setSelectedGroup({ ...group })
                                            setGroupDetailsOpen(true)
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </TableCell>
                                <TableCell className={tableClasses.tableCell} align="center">
                                    {group && !group.is_admin && (
                                        <IconButton
                                            aria-label="delete"
                                            onClick={() => {
                                                setSelectedGroup({ ...group })
                                                setDeleteConfirmationOpen(true)
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }, [data?.body, error, isLoading, searchTerm, tableClasses.tableCell])

    return (
        <SettingsLayout>
            <SettingsSection header={gettext('Groups')}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        value={searchTerm}
                        variant="outlined"
                        size="small"
                        placeholder={gettext('Search')}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="disabled" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm
                                ? <InputAdornment position="end">
                                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                                          <CancelIcon color="disabled" />
                                      </IconButton>
                                  </InputAdornment>
                                : null,
                        }}
                    />

                    <Button
                        variant="outlined"
                        color="primary"
                        sx={{ flexShrink: 0 }}
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setIsCreateGroup(true)
                            setGroupDetailsOpen(true)
                        }}
                    >
                        {gettext('Create group')}
                    </Button>
                </Box>

                {renderGroups()}
            </SettingsSection>

            <GroupDetail
                open={groupDetailsOpen}
                isCreateGroup={isCreateGroup}
                accountOwnerId={accountOwnerId}
                groupId={selectedGroup?.id}
                onClose={async (newGroup) => {
                    if (newGroup) {
                        // TODO: Refetch group
                    }
                    setGroupDetailsOpen(false)
                    setSelectedGroup(null)
                }}
            />

            <GroupDeleteConfirm
                accountOwnerId={accountOwnerId}
                open={deleteConfirmationOpen}
                group={selectedGroup}
                onClose={() => {
                    setDeleteConfirmationOpen(false)
                    // TODO: Refetch groups
                }}
            />
        </SettingsLayout>
    )
}

function filterGroups(groups, searchTerm) {
    return searchTerm
        ? groups.filter((group) => group.name.toLowerCase().includes(searchTerm.toLowerCase()))
        : groups
}
